import React, { useState } from "react";
import "./MobileParameterPanel.css";

interface MobileParameterPanelProps {
  selectedProduct: string;
  stake: number;
  onStakeChange: (stake: number) => void;
  onBuy: () => void;
}

const PRODUCT_NAMES: Record<string, string> = {
  "rise-fall": "Rise/Fall Trade",
  "higher-lower": "Higher/Lower Trade",
  "matches-differs": "Matches/Differs Trade",
  "even-odd": "Even/Odd Trade",
  "over-under": "Over/Under Trade",
  accumulator: "Accumulator Trade",
};

const MobileParameterPanel: React.FC<MobileParameterPanelProps> = ({
  selectedProduct,
  stake,
  onStakeChange,
  onBuy
}) => {
  const [showParameterModal, setShowParameterModal] = useState(false);
  const [selectedDirection, setSelectedDirection] = useState<"rise" | "fall">(
    "rise"
  );
  const [selectedDigit, setSelectedDigit] = useState(5);
  const [tickCount] = useState(5);
  const [duration] = useState("15 minutes");

  const productName =
    PRODUCT_NAMES[selectedProduct] || PRODUCT_NAMES["rise-fall"];

  const payout = {
    stake,
    commission: stake * 0.01,
    potentialPayout: stake * 1.9,
    potentialProfit: stake * 1.9 - stake,
    profitPercentage: (((stake * 1.9 - stake) / stake) * 100).toFixed(0),
  };

  // Product-specific configurations (matches wireframe)
  const getProductConfig = () => {
    // Normalize the product name for consistent checking
    const productKey = selectedProduct
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z-]/g, "");

    switch (productKey) {
      case "rise-fall":
        return {
          showDirection: true,
          showAllowEquals: true,
          showDigitGrid: false,
          directions: { first: "Rise", second: "Fall" },
          parameters: ["Duration", "Stake"],
        };
      case "higher-lower":
      case "higherlower":
        return {
          showDirection: true,
          showAllowEquals: false,
          showDigitGrid: false,
          directions: { first: "Higher", second: "Lower" },
          parameters: ["Barrier", "Duration", "Stake"],
        };
      case "matches-differs":
      case "matchesdiffers":
        return {
          showDirection: true,
          showAllowEquals: false,
          showDigitGrid: true,
          directions: { first: "Matches", second: "Differs" },
          parameters: ["Ticks", "Stake"],
        };
      case "even-odd":
      case "evenodd":
        return {
          showDirection: true,
          showAllowEquals: false,
          showDigitGrid: false,
          directions: { first: "Even", second: "Odd" },
          parameters: ["Ticks", "Stake"],
        };
      case "over-under":
      case "overunder":
        return {
          showDirection: true,
          showAllowEquals: false,
          showDigitGrid: true,
          directions: { first: "Over", second: "Under" },
          parameters: ["Ticks", "Stake"],
        };
      case "accumulator":
        return {
          showDirection: false,
          showAllowEquals: false,
          showDigitGrid: false,
          directions: { first: "", second: "" },
          parameters: ["Growth Rate", "Take Profit", "Stake"],
        };
      default:
        return {
          showDirection: true,
          showAllowEquals: false,
          showDigitGrid: false,
          directions: { first: "Rise", second: "Fall" },
          parameters: ["Duration", "Stake"],
        };
    }
  };

  const config = getProductConfig();

  const getDirectionClass = (directionType: "first" | "second") => {
    const isRiseType = directionType === "first";
    return isRiseType ? "rise" : "fall";
  };

  const getDirectionPayout = () => {
    return payout.potentialPayout.toFixed(2);
  };

  return (
    <>
      <section className="mobile-parameter-panel">
        <div className="parameter-title">{productName}</div>

        <div className="core-parameters">
          {/* Direction Selection (varies by product) */}
          {config.showDirection && (
            <div className="direction-row">
              <button
                className={`direction-btn ${selectedDirection === "rise" ? "active" : ""} ${getDirectionClass("first")}`}
                onClick={() => setSelectedDirection("rise")}
              >
                <span className="direction-label">
                  {config.directions.first}
                </span>
                <span className="direction-payout">
                  ${getDirectionPayout()}
                </span>
              </button>
              <button
                className={`direction-btn ${selectedDirection === "fall" ? "active" : ""} ${getDirectionClass("second")}`}
                onClick={() => setSelectedDirection("fall")}
              >
                <span className="direction-label">
                  {config.directions.second}
                </span>
                <span className="direction-payout">
                  ${getDirectionPayout()}
                </span>
              </button>
            </div>
          )}

          {/* Parameter Buttons (Dynamic based on product) */}
          <div
            className={`parameter-buttons ${config.parameters.length === 3 ? "three-column" : ""}`}
          >
            {config.parameters.map((param) => (
              <button
                key={param}
                className="parameter-btn"
                onClick={() => setShowParameterModal(true)}
              >
                <span className="parameter-label">{param}</span>
                <span className="parameter-value">
                  {param === "Duration" && duration}
                  {param === "Stake" && `$${stake.toFixed(2)}`}
                  {param === "Ticks" && `${tickCount} ticks`}
                  {param === "Barrier" && "+20 pips"}
                  {param === "Growth Rate" && "2% per tick"}
                  {param === "Take Profit" && "Optional"}
                </span>
                <span className="parameter-icon">ⓘ</span>
              </button>
            ))}
          </div>

          {/* Digit Prediction Grid (Digits products only) */}
          {config.showDigitGrid && (
            <div className="digit-prediction">
              <div className="digit-grid">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <button
                    key={digit}
                    className={`digit-btn ${selectedDigit === digit ? "selected" : ""}`}
                    onClick={() => setSelectedDigit(digit)}
                  >
                    {digit}
                  </button>
                ))}
              </div>
              <div className="digit-help">
                Select the digit you predict ({selectedProduct} only)
              </div>
            </div>
          )}
        </div>

        <button className="buy-button" onClick={onBuy}>
          Buy
        </button>
      </section>

      {/* Parameter Modal */}
      {showParameterModal && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowParameterModal(false)}
        >
          <div className="mobile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trade Parameters</h3>
              <button
                className="modal-close"
                onClick={() => setShowParameterModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              {/* Dynamic parameters based on selected product */}
              {config.parameters.map((param) => (
                <div key={param} className="mobile-parameter-group">
                  <label>{param}</label>
                  {param === "Stake" && (
                    <input
                      type="number"
                      className="mobile-input"
                      value={stake}
                      onChange={(e) => onStakeChange(Number(e.target.value))}
                      min="1"
                      max="5000"
                    />
                  )}
                  {param === "Duration" && (
                    <select className="mobile-input" defaultValue="15 minutes">
                      <option>1 minute</option>
                      <option>5 minutes</option>
                      <option>15 minutes</option>
                      <option>30 minutes</option>
                      <option>1 hour</option>
                    </select>
                  )}
                  {param === "Ticks" && (
                    <select className="mobile-input" defaultValue="5">
                      <option>1 tick</option>
                      <option>3 ticks</option>
                      <option>5 ticks</option>
                      <option>10 ticks</option>
                      <option>25 ticks</option>
                    </select>
                  )}
                  {param === "Barrier" && (
                    <input
                      type="text"
                      className="mobile-input"
                      defaultValue="+20 pips"
                      placeholder="Enter barrier"
                    />
                  )}
                  {param === "Growth Rate" && (
                    <select className="mobile-input" defaultValue="2%">
                      <option>1% per tick</option>
                      <option>2% per tick</option>
                      <option>3% per tick</option>
                      <option>4% per tick</option>
                      <option>5% per tick</option>
                    </select>
                  )}
                  {param === "Take Profit" && (
                    <input
                      type="number"
                      className="mobile-input"
                      placeholder="Optional (e.g. $100)"
                    />
                  )}
                </div>
              ))}

              <button
                className="modal-save-btn"
                onClick={() => setShowParameterModal(false)}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileParameterPanel;
export { MobileParameterPanel };
